import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import { CircularProgress } from '@material-ui/core'

import SlideInModal from './ui/slideInModal'
import PlanDetails from './ui/addOnRenewal/PlanDetails'
import PaymentStep from './ui/addOnRenewal/PaymentStep'
import CardSetupStep from './ui/addOnRenewal/CardSetupStep'
import SigningStep from './ui/addOnRenewal/SigningStep'
import Confirmation from './ui/addOnRenewal/Confirmation'
import { BILLING_CARD, BILLING_INVOICE, PAYER_PRIMARY, PAYER_SELF } from './ui/addOnRenewal/addOnRenewalHelpers'
import { genHex } from './shared/genericFunctions'
import {
    enrollAddOnRenewal,
    getAddOnPaymentMethod,
    getAddOnRenewal,
    recordAddOnRenewalStep,
    verifyAddOnRenewalSignature
} from '../store/actions/addOnRenewalActions'
import ConfirmDetails from './shared/ConfirmDetails'

export const STEPS = {
    loading: 'loading',
    plan: 'plan',
    payment: 'payment',
    card: 'card',
    details: 'details',
    sign: 'sign',
    verify: 'verify',
    enroll: 'enroll',
    done: 'done'
}

// What each step reports as lastStepReached. 'docusign' is written by the
// server when the envelope is created; verify has no step of its own.
const STEP_RECORDED = {
    [STEPS.plan]: 'plan',
    [STEPS.payment]: 'payment',
    [STEPS.card]: 'card',
    [STEPS.details]: 'details',
    [STEPS.enroll]: 'enroll'
}

const CLOSED_STATUSES = ['cancelled', 'expired']

/**
 * The add-on member's renewal (add-on renewal spec, section 6), at
 * /addon-renewal/:renewalId. The entry links from the renewal emails land
 * here after login, signup or a one-click token.
 *
 *   plan details -> payment (primary pays, card on file, or card entry)
 *   -> DocuSign -> server verifies the signature -> enroll -> confirmation
 *
 * A renewal that is already signed resumes at the payment choice and then
 * enrolls; an enrolled one shows the confirmation.
 */
const AddOnRenewal = () => {
    const { renewalId } = useParams()
    const history = useHistory()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const loginToken = localStorage.getItem('loginToken')

    const [renewal, setRenewal] = useState(null)
    const [step, setStep] = useState(STEPS.loading)
    // Contract-only: collected on the details step, never saved.
    const [placeOfBirth, setPlaceOfBirth] = useState('')
    const [payerOptions, setPayerOptions] = useState([])
    const [payer, setPayer] = useState(null)
    const [billing, setBilling] = useState(null)
    const [paymentMethod, setPaymentMethod] = useState(null)
    const [signed, setSigned] = useState(false)
    const [notice, setNotice] = useState(null)
    const [cardNotice, setCardNotice] = useState(null)
    const [signingNotice, setSigningNotice] = useState(null)
    const [signingKey, setSigningKey] = useState(0)
    const [verifyState, setVerifyState] = useState(null)
    const [enrollState, setEnrollState] = useState(null)
    const [enrolledPayer, setEnrolledPayer] = useState(null)
    const [enrolledBilling, setEnrolledBilling] = useState(null)

    // Matched against what /docusign-bridge posts back, as in myAccount.
    const nonce = useMemo(() => genHex(), [])
    const sid = useMemo(() => crypto.randomUUID(), [])

    const payerRef = useRef(payer)
    payerRef.current = payer
    const billingRef = useRef(billing)
    billingRef.current = billing

    const goHome = () => history.push('/')

    // -- load --------------------------------------------------------------

    useEffect(() => {
        dispatch(getAddOnRenewal(loginToken, renewalId, (err, data) => {
            if (err || !data) {
                enqueueSnackbar((err && err.message) || "We couldn't open this renewal", { variant: 'error' })
                history.replace('/')
                return
            }
            if (data.viewer !== 'addOn') {
                enqueueSnackbar('Only the add-on member can complete this renewal', { variant: 'error' })
                history.replace('/')
                return
            }
            if (CLOSED_STATUSES.indexOf(data.status) !== -1) {
                enqueueSnackbar(`This renewal is ${data.status}`, { variant: 'error' })
                history.replace('/')
                return
            }

            const options = data.payerOptions && data.payerOptions.length ? data.payerOptions : [PAYER_SELF]
            setRenewal(data)
            setPayerOptions(options)

            if (data.status === 'enrolled') {
                setEnrolledPayer(data.payer)
                setEnrolledBilling(data.billing || null)
                setStep(STEPS.done)
                return
            }

            // Preselect only what the member can pick: their saved choice; else
            // the primary, when the primary has opted to pay (the member can
            // still switch to paying themselves); a lone option is self.
            if (data.payer && options.indexOf(data.payer) !== -1) {
                setPayer(data.payer)
            } else if (options.indexOf(PAYER_PRIMARY) !== -1) {
                setPayer(PAYER_PRIMARY)
            } else if (options.length === 1) {
                setPayer(options[0])
            }

            if (data.status === 'signed') {
                setSigned(true)
                setNotice('Your contract is signed. Choose how to pay to finish your renewal.')
                setStep(STEPS.payment)
                return
            }
            setStep(STEPS.plan)
        }))
        // Loaded once per renewal.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renewalId])

    // -- lastStepReached -----------------------------------------------------

    useEffect(() => {
        if (renewal && STEP_RECORDED[step]) {
            dispatch(recordAddOnRenewalStep(loginToken, renewalId, STEP_RECORDED[step]))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, renewal])

    // -- card on file (self-pay) --------------------------------------------

    useEffect(() => {
        if (payer !== PAYER_SELF || paymentMethod || !renewal) { return }
        dispatch(getAddOnPaymentMethod(loginToken, renewalId, (err, data) => {
            // Default billing: autopay with the card on file, else an invoice.
            // An add-on with no Stripe customer has no card; and if the check
            // fails, invoice is still a safe default (it needs no card), so
            // the member is never stuck here.
            const found = !err && data ? data : { hasDefaultPaymentMethod: false, card: null }
            setPaymentMethod(found)
            setBilling((current) => current || (found.hasDefaultPaymentMethod ? BILLING_CARD : BILLING_INVOICE))
        }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payer, renewal, paymentMethod])

    // -- enroll --------------------------------------------------------------

    const enroll = () => {
        const chosen = payerRef.current
        const chosenBilling = chosen === PAYER_SELF ? (billingRef.current || BILLING_CARD) : null
        setStep(STEPS.enroll)
        setEnrollState({ status: 'enrolling' })

        dispatch(enrollAddOnRenewal(loginToken, renewalId, chosen, (err, data) => {
            if (!err) {
                setEnrolledPayer((data && data.payer) || chosen)
                setEnrolledBilling((data && data.billing) || chosenBilling)
                setEnrollState(null)
                setStep(STEPS.done)
                return
            }
            if (err.code === 'no_payment_method') {
                setPayer(PAYER_SELF)
                setBilling(BILLING_CARD)
                setPaymentMethod({ hasDefaultPaymentMethod: false, card: null })
                setCardNotice("We couldn't find a card on file. Add one to finish your renewal.")
                setEnrollState(null)
                setStep(STEPS.card)
                return
            }
            if (err.code === 'primary_not_authorized') {
                setPayerOptions([PAYER_SELF])
                setPayer(PAYER_SELF)
                setNotice('Your primary member is no longer covering this plan. Please choose how to pay.')
                setEnrollState(null)
                setStep(STEPS.payment)
                return
            }
            setEnrollState({
                status: 'error',
                message: err.status === 502
                    ? "We couldn't set up your billing with our payment processor. Please try again."
                    : (err.message || "We couldn't complete your renewal. Please try again.")
            })
        }, chosenBilling))
    }

    // -- verify the signature on the server ----------------------------------

    const verify = () => {
        setStep(STEPS.verify)
        setVerifyState({ status: 'verifying' })
        dispatch(verifyAddOnRenewalSignature(loginToken, renewalId, (err) => {
            if (!err) {
                setSigned(true)
                setVerifyState(null)
                enroll()
                return
            }
            setVerifyState({ status: err.status === 409 ? 'unconfirmed' : 'error', message: err.message })
        }))
    }

    // -- DocuSign bridge messages ---------------------------------------------

    const onMessageRef = useRef(null)
    onMessageRef.current = (ev) => {
        if (ev.origin !== window.location.origin) return
        const msg = ev.data || {}
        if (msg.nonce !== nonce) return
        if (!msg.sid || msg.sid !== sid) return
        if (msg.type !== 'DOCUSIGN_DONE' && msg.type !== 'DOCUSIGN_EVENT') return

        if (msg.type === 'DOCUSIGN_DONE' && msg.event === 'signing_complete') {
            verify()
        } else {
            setSigningNotice('The contract was not completed, please try again')
            setSigningKey((k) => k + 1)
            setStep(STEPS.sign)
        }
    }

    useEffect(() => {
        const listener = (ev) => onMessageRef.current(ev)
        window.addEventListener('message', listener)
        return () => window.removeEventListener('message', listener)
    }, [])

    // -- step transitions -----------------------------------------------------

    const continueFromPayment = () => {
        if (payer === PAYER_SELF && billing === BILLING_CARD && !(paymentMethod && paymentMethod.hasDefaultPaymentMethod)) {
            setCardNotice(null)
            setStep(STEPS.card)
            return
        }
        if (signed) {
            enroll()
            return
        }
        setStep(STEPS.details)
    }

    const onCardSaved = (data) => {
        setPaymentMethod({ hasDefaultPaymentMethod: true, card: (data && data.card) || null })
        setBilling(BILLING_CARD)
        enqueueSnackbar('Card saved', { variant: 'success' })
        if (signed) {
            enroll()
            return
        }
        setStep(STEPS.details)
    }

    const changePayer = (value) => {
        setPayer(value)
    }

    // -- render -----------------------------------------------------------------

    const back = (to) => <Button size="small" onClick={() => setStep(to)}>Back</Button>

    const body = () => {
        switch (step) {
            case STEPS.loading:
                return <Grid container justifyContent="center"><CircularProgress /></Grid>

            case STEPS.plan:
                return <PlanDetails plan={renewal.plan} termStart={renewal.termStart} termEnd={renewal.termEnd} onContinue={() => setStep(STEPS.payment)} />

            case STEPS.payment:
                return <>
                    <PaymentStep
                        payerOptions={payerOptions}
                        primaryName={renewal.primaryName}
                        plan={renewal.plan}
                        termStart={renewal.termStart}
                        payer={payer}
                        onPayerChange={changePayer}
                        paymentMethod={paymentMethod}
                        billing={billing}
                        onBillingChange={setBilling}
                        onContinue={continueFromPayment}
                        continueLabel={signed ? 'Complete Renewal' : 'Continue'}
                    />
                    {!signed && back(STEPS.plan)}
                </>

            case STEPS.card:
                return <>
                    <CardSetupStep loginToken={loginToken} renewalId={renewalId} onSaved={onCardSaved} notice={cardNotice} />
                    {back(STEPS.payment)}
                </>

            case STEPS.details:
                return <>
                    <ConfirmDetails
                        continueLabel="Save and continue to signing"
                        onConfirmed={({ placeOfBirth: pob }) => {
                            setPlaceOfBirth(pob)
                            setSigningNotice(null)
                            setStep(STEPS.sign)
                        }}
                    />
                    {back(STEPS.payment)}
                </>
            case STEPS.sign:
                return <>
                    <SigningStep key={signingKey} loginToken={loginToken} renewalId={renewalId} nonce={nonce} sid={sid} plan={renewal.plan} notice={signingNotice}
                        placeOfBirth={placeOfBirth}
                        onDetailsIncomplete={() => setStep(STEPS.details)} />
                    {back(STEPS.details)}
                </>

            case STEPS.verify:
                if (verifyState && verifyState.status === 'verifying') {
                    return <span>Confirming your signature <CircularProgress size={'14px'} /></span>
                }
                return <Grid container direction="column" spacing={2}>
                    <Grid item>
                        <Typography data-testid="verify-failed">
                            {verifyState && verifyState.status === 'unconfirmed'
                                ? "We couldn't confirm your signature yet. It can take a moment to reach us."
                                : "We couldn't check your signature right now."}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Button variant="contained" color="primary" onClick={verify}>Retry</Button>
                    </Grid>
                </Grid>

            case STEPS.enroll:
                if (!enrollState || enrollState.status === 'enrolling') {
                    return <span>Finishing your renewal <CircularProgress size={'14px'} /></span>
                }
                return <Grid container direction="column" spacing={2}>
                    <Grid item>
                        <Typography data-testid="enroll-failed" style={{ color: 'crimson' }}>{enrollState.message}</Typography>
                    </Grid>
                    <Grid item>
                        <Button variant="contained" color="primary" onClick={enroll}>Try again</Button>
                    </Grid>
                </Grid>

            case STEPS.done:
                return <Confirmation plan={renewal.plan} termStart={renewal.termStart} termEnd={renewal.termEnd} payer={enrolledPayer || payer} billing={enrolledBilling || billing} primaryName={renewal.primaryName} onDone={goHome} />

            default:
                return null
        }
    }

    const showNotice = notice && (step === STEPS.payment)

    // The step bar: card entry belongs to Payment; verify and enroll to Sign.
    const FLOW_STEPS = ['Plan', 'Payment', 'Details', 'Sign', 'Done']
    const activeFlowStep = {
        [STEPS.loading]: 0, [STEPS.plan]: 0,
        [STEPS.payment]: 1, [STEPS.card]: 1,
        [STEPS.details]: 2,
        [STEPS.sign]: 3, [STEPS.verify]: 3, [STEPS.enroll]: 3,
        [STEPS.done]: 4
    }[step] || 0
    const subtitle = renewal && renewal.plan
        ? [renewal.plan.name, renewal.primaryName ? `${renewal.primaryName}'s household` : null].filter(Boolean).join(' · ')
        : null

    return <SlideInModal openModal={true} handleClose={goHome}
        title="Renew your add-on membership" subtitle={subtitle}
        steps={FLOW_STEPS} activeStep={activeFlowStep}>
        <Grid container spacing={3} direction="column">
            {showNotice && <Grid container item justifyContent="center">
                <Typography data-testid="flow-notice">{notice}</Typography>
            </Grid>}
            <Grid item>{body()}</Grid>
        </Grid>
    </SlideInModal>
}

export default AddOnRenewal
